import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';
const ControlAuthContext = createContext(null);

export const ControlAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [authError, setAuthError] = useState('');

  const logout = useCallback(() => {
    localStorage.removeItem('controlToken');
    setUser(null);
    setAuthError('');
  }, []);

  const login = useCallback(async (userid, password) => {
    setAuthenticating(true);
    setAuthError('');
    try {
      const response = await axios.post(`${API_BASE}/api/control-login`, {
        userid: userid.trim(),
        password,
      }, { timeout: 15000 });
      const { token, user: authenticatedUser } = response.data || {};
      if (!token || !authenticatedUser) throw new Error('The server returned an incomplete session.');
      localStorage.setItem('controlToken', token);
      setUser({ ...authenticatedUser, accesstype: Number(authenticatedUser.accesstype) });
      return { success: true };
    } catch (error) {
      localStorage.removeItem('controlToken');
      const message = error.response?.status === 429
        ? 'Too many login attempts. Please wait a few minutes and try again.'
        : error.response?.data?.error || (error.code === 'ECONNABORTED'
          ? 'The login request timed out. Please try again.'
          : 'User ID or password is incorrect.');
      setAuthError(message);
      return { success: false, error: message };
    } finally {
      setAuthenticating(false);
    }
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        if (error.response?.status === 401 && url.includes('/api/control/')) logout();
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('controlToken');
      if (!token || token === 'null' || token === 'undefined') {
        localStorage.removeItem('controlToken');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE}/api/control-verify`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        setUser({ ...response.data.user, accesstype: Number(response.data.user.accesstype) });
      } catch (error) {
        localStorage.removeItem('controlToken');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, []);

  const value = useMemo(() => ({ user, login, logout, loading, authenticating, authError }),
    [user, login, logout, loading, authenticating, authError]);

  return <ControlAuthContext.Provider value={value}>{children}</ControlAuthContext.Provider>;
};

export const useControlAuth = () => {
  const context = useContext(ControlAuthContext);
  if (!context) throw new Error('useControlAuth must be used inside ControlAuthProvider');
  return context;
};
